/**
 * Money string formatting and parsing for the scholarship forms.
 *
 * Kept out of `MoneyInput.tsx` so that file exports a component and nothing
 * else, which is what React Fast Refresh needs to hot-reload it reliably.
 */

/**
 * Reformats a free-typed money string with thousand-separators on every
 * keystroke. Accepts digits + an optional single decimal point (max 2 places);
 * everything else is silently stripped. Caller stores the formatted string in
 * state and uses {@link parseMoneyString} to extract a number on submit.
 */
export const formatMoneyString = (raw: string): string => {
  let cleaned = raw.replace(/[^\d.]/g, '');
  // Keep only the first decimal point if the user typed several.
  const firstDot = cleaned.indexOf('.');
  if (firstDot >= 0) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, '');
  }
  const [intPart = '', decPart] = cleaned.split('.');
  const limitedDec = decPart !== undefined ? decPart.slice(0, 2) : undefined;
  // Strip leading zeros but keep a single "0" so typing "0.5" works.
  const intStripped = intPart.replace(/^0+(?=\d)/, '');
  const intWithCommas = intStripped.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (limitedDec !== undefined) {
    return `${intWithCommas || '0'}.${limitedDec}`;
  }
  return intWithCommas;
};

/** Strips commas and converts to a number; '' → null, garbage → null. */
export const parseMoneyString = (s: string): number | null => {
  const cleaned = s.replace(/,/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/** Formats a number for initial display (e.g. when editing an existing player). */
export const numberToMoneyString = (n: number | null | undefined): string =>
  n == null ? '' : formatMoneyString(n.toString());
